// Base API Configuration
// Central configuration for all API calls
import { API_BASE_URL } from '../config/api';
import { refreshTokens } from '../config/auth';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) return refreshPromise;
  isRefreshing = true;
  refreshPromise = (async () => {
    const result = await refreshTokens();
    isRefreshing = false;
    refreshPromise = null;
    return result !== null;
  })();
  return refreshPromise;
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('authToken');

  const config: RequestInit = {
    ...options,
    headers: {
      ...options.headers,
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(!(options.body instanceof FormData) && {
        'Content-Type': 'application/json',
      }),
    },
  };

  try {
    let response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (response.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        const newToken = localStorage.getItem('authToken');
        const retryConfig: RequestInit = {
          ...options,
          headers: {
            ...options.headers,
            ...(newToken && { Authorization: `Bearer ${newToken}` }),
            ...(!(options.body instanceof FormData) && {
              'Content-Type': 'application/json',
            }),
          },
        };
        response = await fetch(`${API_BASE_URL}${endpoint}`, retryConfig);
      }

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/';
        throw new Error('Authentication required');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string) => apiRequest<T>(endpoint, { method: 'GET' }),
  
  post: <T>(endpoint: string, data?: any) =>
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
  
  put: <T>(endpoint: string, data?: any) =>
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: <T>(endpoint: string) => apiRequest<T>(endpoint, { method: 'DELETE' }),
};

// Authentication helpers
export const auth = {
  isAuthenticated: () => !!localStorage.getItem('authToken'),

  setToken: (token: string) => localStorage.setItem('authToken', token),

  clearToken: () => localStorage.removeItem('authToken'),

  getToken: () => localStorage.getItem('authToken'),

  getAuthHeaders: (): Record<string, string> => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
};

export default api;